import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import {
  FLEET_DEFAULT_DAYS,
  parseFleetActivationFilters,
  useFleetActivations,
  type FleetActivationSummary,
} from "@visioni/a3s-monitoring/app";
import { EMPTY } from "../format";
import { activationHref } from "../routes";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { useListTimeZone } from "../hooks/useListTimeZone";
import { OrderField } from "../components/OrderField";
import { TimeBoundFields } from "../components/TimeBoundFields";
import { TimeZoneField } from "../components/TimeZoneField";
import { ActivationsTable } from "../components/ActivationsTable";
import { ClientNumberFilter, MatchedClients, NO_MATCHED_CLIENTS } from "../components/ClientNumberFilter";
import { QueryNotice } from "../components/QueryNotice";

const rawId = (id: string) => id;
const blank = (value: string | undefined) => (value === "" ? undefined : value);
// The fleet list has no site, so Default is always the viewer's own zone.
const noSiteTimeZone = () => undefined;

/**
 * Every activation in the replica, across tenants, filtered by time and by the
 * start of a client number — the fleet index knows nothing of sites or cameras.
 * With no site to take a zone from, the range defaults to the viewer's own
 * (`?tz=&from=&to=&order=&clientNo=`).
 *
 * A row whose tenant the directory holds links to that tenant's activation
 * page; the rest — unattributed, or a tenant this stack has never heard of —
 * link to the fleet detail page. Site and camera show as ids: the app only
 * names the selected tenant's.
 */
export function FleetActivationsScreen() {
  const router = useRouter();
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const params = useLocalSearchParams<{ from?: string; to?: string; tz?: string; order?: string; clientNo?: string }>();
  const from = blank(params.from);
  const to = blank(params.to);
  const order = blank(params.order);
  const clientNo = blank(params.clientNo);
  const { timeZone, fallback } = useListTimeZone({
    tz: blank(params.tz),
    site: undefined,
    siteTimeZone: noSiteTimeZone,
    onResolve: (tz) => router.setParams({ tz }),
  });
  const parsed =
    timeZone === undefined ? undefined : (
      parseFleetActivationFilters({ from, to, tz: timeZone, order, clientNo }, new Date())
    );

  const list = useFleetActivations({
    filters: parsed?.ok === true ? parsed.filters : { since: "" },
    enabled: parsed?.ok === true,
  });

  const tenantName = useDirectoryTenantName();
  const tenantLabel = (activation: FleetActivationSummary) =>
    activation.a3sTenant === undefined ? EMPTY : (tenantName(activation.a3sTenant) ?? activation.a3sTenant);
  const knownTenant = (activation: FleetActivationSummary) =>
    activation.a3sTenant !== undefined && tenantName(activation.a3sTenant) !== undefined ?
      activation.a3sTenant
    : undefined;

  return (
    <View style={styles.page}>
      {timeZone !== undefined && (
        <View style={styles.filters}>
          <View style={styles.bounds}>
            <TimeBoundFields
              // A defaulted start stays blank, so its placeholder names the
              // default and clearing a chosen day goes back to it.
              value={{ from, to }}
              onChange={(next) => router.setParams({ from: next.from, to: next.to })}
              fromPlaceholder={`Last ${String(FLEET_DEFAULT_DAYS)} days`}
              // Beside the bounds it is read in.
              leading={
                <TimeZoneField
                  hasSiteFilter={false}
                  value={timeZone}
                  fallback={fallback}
                  at={parsed?.ok === true ? new Date(parsed.filters.since) : new Date()}
                  onChange={(tz) => router.setParams({ tz })}
                />
              }
            />
          </View>
          <View style={styles.narrowing}>
            <View style={styles.order}>
              <OrderField value={order} onChange={(next) => router.setParams({ order: next })} />
            </View>
            <ClientNumberFilter
              clientNo={clientNo}
              onChange={(next) => router.setParams({ clientNo: next })}
              rows="activations"
            />
          </View>
          <MatchedClients rows="Activations" clientNos={list.clientNos} />
        </View>
      )}
      {parsed === undefined || timeZone === undefined ?
        <View style={styles.notice}>
          <QueryNotice isLoading error={null} onRetry={list.refetch} />
        </View>
      : !parsed.ok ?
        <View style={styles.notice}>
          <Banner variant="warning" title="Filters not applied" message={parsed.problem} />
        </View>
      : list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.clientNos?.length === 0 ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            {NO_MATCHED_CLIENTS}
          </Text>
        </View>
      : list.items.length === 0 && list.nextCursor === null && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            No activations found
          </Text>
        </View>
      : <ActivationsTable
          items={list.items}
          isStale={list.isPlaceholderData}
          isFetching={list.isFetching}
          hrefFor={(activation) => activationHref(tenantId, activation.ticketNo, knownTenant(activation))}
          clientLabel={(activation) => activation.clientNo ?? EMPTY}
          tenantLabel={tenantLabel}
          siteLabel={rawId}
          cameraLabel={rawId}
          timeZone={timeZone}
          pager={list.pager}
          nextCursor={list.nextCursor}
        />
      }
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filters: {
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.xl,
    // Above the list, so the pickers open over the rows.
    zIndex: 1,
  },
  // Above the order field, so the timezone list and calendars open over it.
  bounds: { zIndex: 1 },
  narrowing: { flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing.lg },
  // The width of the timezone field above it.
  order: { flexBasis: "24%", maxWidth: 420, minWidth: 220 },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
}));
